import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import { X, BookmarkSimple } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { getErrorMessage, workflowTemplatesApi } from '@/lib/api'
import { WORKFLOW_CATEGORY_LABELS, type WorkflowTemplateCategory } from '@/pages/templates/workflowTemplates'
import { useScrollLock } from '@/lib/useScrollLock'

const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }
const labelClass = 'block mb-1.5 text-muted-foreground dark:text-[#8a8f98]'
const inputClass =
  'w-full px-3 py-2 rounded-md border border-border/60 bg-background text-[13px] text-foreground ' +
  'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#5e6ad2] ' +
  'dark:bg-white/[0.03] dark:border-white/[0.08] dark:text-[#f7f8f8]'

interface SaveAsTemplateDialogProps {
  isOpen: boolean
  onClose: () => void
  defaultName: string
  defaultDescription: string
  nodes: unknown[]
  edges: unknown[]
  /** Called after a successful save, in addition to onClose. */
  onSaved?: () => void
}

/**
 * "Save as template" -- lets Owner/Admin/Manager turn the workflow currently
 * open in the builder into a reusable, account-scoped template that shows
 * up in the Templates screen (WorkflowTemplateGallery.tsx) alongside the
 * built-in catalog. See docs/superpowers/specs/2026-08-21-workflow-templates-design.md.
 */
export const SaveAsTemplateDialog: React.FC<SaveAsTemplateDialogProps> = ({
  isOpen,
  onClose,
  defaultName,
  defaultDescription,
  nodes,
  edges,
  onSaved,
}) => {
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState(defaultDescription)
  const [category, setCategory] = useState<WorkflowTemplateCategory>('cold_outreach')
  const [isSaving, setIsSaving] = useState(false)
  useScrollLock(isOpen)

  if (!isOpen) return null

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Please enter a template name')
      return
    }
    setIsSaving(true)
    try {
      await workflowTemplatesApi.create({
        name: trimmedName,
        description: description.trim(),
        category,
        nodes,
        edges,
      })
      toast.success('Saved as template')
      onSaved?.()
      onClose()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save as template'))
    } finally {
      setIsSaving(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Save as template"
    >
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={onClose}
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        className="relative z-10 bg-card border border-border/60 rounded-2xl shadow-2xl dark:bg-[#0f1011] dark:border-white/[0.08] p-6 w-full max-w-md"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-foreground dark:text-[#f7f8f8] flex items-center gap-2"
            style={{ fontSize: '18px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
          >
            <BookmarkSimple className="h-5 w-5 text-[#5e6ad2]" weight="fill" />
            Save as template
          </h2>
          <button
            onClick={onClose}
            aria-label="Close save-as-template dialog"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
          >
            <X className="h-4 w-4" weight="regular" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="template-name" className={labelClass} style={labelStyle}>
              Template name
            </label>
            <input
              id="template-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="template-description" className={labelClass} style={labelStyle}>
              Description (optional)
            </label>
            <textarea
              id="template-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="template-category" className={labelClass} style={labelStyle}>
              Category
            </label>
            <Select
              id="template-category"
              value={category}
              onChange={(v) => setCategory(v as WorkflowTemplateCategory)}
              options={Object.entries(WORKFLOW_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="default" size="sm" loading={isSaving} onClick={handleSubmit}>
            Save template
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}
